import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListFilterIcon, TagIcon, CircleDotIcon, SparklesIcon } from 'lucide-react'
import {
  useListCustomAgentConfigsQuery,
  useListCustomAgentRunsQuery,
  useListNotificationRulesQuery,
  useListResourceLabelsQuery
} from '@axilon-apps/domains-ontology-ui-state'

import {
  MultiSelectFilter,
  LabelPill,
  catalogFromLabelLists,
  mergeLabelCatalogs
} from '../../../components/resource-labels'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import { FleetSummary } from './FleetSummary'
import { SortMenu, type SortOption } from './SortMenu'
import { CreateConfigActions } from './CreateConfigActions'
import { activityFor, buildActivityMap, compareRecentFirst } from '../util/activity'
import { formatRelativeTime } from '../../../lib/time'
import { cn } from '../../../lib/utils'
import type { ConfigureType } from '../ConfigurePage'
import { readListPrefs, writeListPrefs, type ConfigureSortKey } from '../list-prefs'
import {
  BOTTLENECK_AI_PROMPT_TEMPLATE,
  CUSTOM_AGENT_AI_PROMPT_TEMPLATE,
  NOTIFICATION_RULES_AI_PROMPT_TEMPLATE,
  PM_AI_PROMPT_TEMPLATE,
  RCA_AI_PROMPT_TEMPLATE,
  SPEC_COVERAGE_AI_PROMPT_TEMPLATE,
  SUMMARY_AI_PROMPT_TEMPLATE,
  YIELD_QUALITY_AI_PROMPT_TEMPLATE
} from '../../AgentsPage/aiPromptTemplates'

type RowType = ConfigureType
type StatusKey = 'active' | 'paused'
type SortKey = ConfigureSortKey

const TYPE_LABELS: Record<RowType, string> = {
  custom: 'Custom agent',
  'notification-rules': 'Notification rule'
}

/**
 * Manual create routes. The specialized RCA/PM/BNA/YQ surfaces are gone —
 * those intents draft a template-seeded custom agent via AI. Notification
 * rules keep a dedicated form.
 */
const MANUAL_CREATE_OPTIONS: ReadonlyArray<{ type: RowType; route: string; label: string }> = [
  {
    type: 'notification-rules',
    route: '/agents/notification-rules/new',
    label: 'Notification rule'
  }
]

/** AI drafts: specialized-like intents create custom agents via templates. */
const AI_DRAFT_OPTIONS: ReadonlyArray<{
  type: RowType
  label: string
  promptTemplate: string
}> = [
  { type: 'custom', label: 'Custom agent (RCA template)', promptTemplate: RCA_AI_PROMPT_TEMPLATE },
  { type: 'custom', label: 'Custom agent (PM template)', promptTemplate: PM_AI_PROMPT_TEMPLATE },
  {
    type: 'custom',
    label: 'Custom agent (bottleneck template)',
    promptTemplate: BOTTLENECK_AI_PROMPT_TEMPLATE
  },
  {
    type: 'custom',
    label: 'Custom agent (yield-quality template)',
    promptTemplate: YIELD_QUALITY_AI_PROMPT_TEMPLATE
  },
  {
    type: 'custom',
    label: 'Custom agent (summary template)',
    promptTemplate: SUMMARY_AI_PROMPT_TEMPLATE
  },
  {
    type: 'custom',
    label: 'Custom agent (spec-coverage template)',
    promptTemplate: SPEC_COVERAGE_AI_PROMPT_TEMPLATE
  },
  { type: 'custom', label: 'Custom agent', promptTemplate: CUSTOM_AGENT_AI_PROMPT_TEMPLATE },
  {
    type: 'notification-rules',
    label: 'Notification rule',
    promptTemplate: NOTIFICATION_RULES_AI_PROMPT_TEMPLATE
  }
]

const PAGE_SIZE = 15

const SORT_OPTIONS: ReadonlyArray<SortOption<SortKey>> = [
  { value: 'name', label: 'Name' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'last_fired', label: 'Last fired' }
]

interface UnifiedRow {
  id: string
  type: RowType
  name: string
  description: string
  state: StatusKey
  labels: string[]
  updatedAt: string
  lastFiredAt: string | null
  builtIn: boolean
}

function statusTone(state: StatusKey): { fg: string; label: string } {
  switch (state) {
    case 'active':
      return { fg: 'text-st-run', label: 'active' }
    case 'paused':
      return { fg: 'text-st-warn', label: 'paused' }
  }
}

/** Custom agents are active|paused; notification rules use disabled for the inactive state. */
function rowState(kind: RowType, state: string): StatusKey {
  if (kind === 'custom') return state === 'paused' ? 'paused' : 'active'
  return state === 'disabled' ? 'paused' : 'active'
}

/**
 * Design redesign: one table across all config families with search,
 * label/status multiselects, sort, and pagination.
 */
export function ConfigUnifiedList() {
  const navigate = useNavigate()
  const { data: customConfigs = [], isLoading: customLoading } =
    useListCustomAgentConfigsQuery(undefined)
  const { data: rules = [], isLoading: rulesLoading } = useListNotificationRulesQuery(undefined)
  const { data: customRuns = [] } = useListCustomAgentRunsQuery(undefined)
  const { data: apiCatalog = [] } = useListResourceLabelsQuery(undefined, {
    refetchOnMountOrArgChange: true
  })

  const [initialPrefs] = useState(readListPrefs)
  const [search, setSearch] = useState(initialPrefs.search)
  const [labelFilter, setLabelFilter] = useState<string[]>(initialPrefs.labels)
  const [statuses, setStatuses] = useState<string[]>(initialPrefs.statuses)
  const [sortBy, setSortBy] = useState<SortKey>(initialPrefs.sortBy)
  const [page, setPage] = useState(0)

  useEffect(() => {
    writeListPrefs({ search, labels: labelFilter, statuses, sortBy })
  }, [search, labelFilter, statuses, sortBy])

  const activity = useMemo(
    () =>
      buildActivityMap(
        customRuns,
        (r) => r.custom_agent_config_id,
        (r) => r.created_at
      ),
    [customRuns]
  )

  const rows = useMemo<UnifiedRow[]>(() => {
    const out: UnifiedRow[] = []
    for (const c of customConfigs) {
      out.push({
        id: c.id,
        type: 'custom',
        name: c.name,
        description: c.usage_description ?? '',
        state: rowState('custom', c.state),
        labels: c.labels ?? [],
        updatedAt: c.updated_at,
        lastFiredAt: activityFor(activity, c.id).lastFiredAt,
        builtIn: Boolean(c.system_key)
      })
    }
    for (const c of rules) {
      out.push({
        id: c.id,
        type: 'notification-rules',
        name: c.display_name,
        description: c.message_title ?? '',
        state: rowState('notification-rules', c.state),
        labels: c.labels ?? [],
        updatedAt: c.updated_at,
        lastFiredAt: c.last_notified_at ?? null,
        builtIn: false
      })
    }
    return out
  }, [customConfigs, rules, activity])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const next = rows.filter((r) => {
      if (statuses.length > 0 && !statuses.includes(r.state)) return false
      if (labelFilter.length > 0 && !labelFilter.every((t) => r.labels.includes(t))) return false
      if (
        q &&
        !(
          r.name.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.labels.some((t) => t.includes(q))
        )
      ) {
        return false
      }
      return true
    })
    next.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'updated':
          return compareRecentFirst(a.updatedAt, b.updatedAt)
        case 'last_fired':
          return compareRecentFirst(a.lastFiredAt, b.lastFiredAt)
        default:
          return 0
      }
    })
    return next
  }, [rows, search, statuses, labelFilter, sortBy])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageSafe = Math.min(page, totalPages - 1)
  const pageRows = filtered.slice(pageSafe * PAGE_SIZE, pageSafe * PAGE_SIZE + PAGE_SIZE)

  const counts = useMemo(() => {
    let active = 0
    let paused = 0
    for (const r of rows) {
      if (r.state === 'active') active++
      else paused++
    }
    return { active, paused, total: rows.length }
  }, [rows])

  const statusOptions = [
    { value: 'active', label: 'Active', count: counts.active },
    { value: 'paused', label: 'Paused', count: counts.paused }
  ]

  const labelOptions = useMemo(() => {
    const merged = mergeLabelCatalogs(
      apiCatalog.filter((e) => e.count > 0).map((e) => ({ label: e.name, count: e.count })),
      catalogFromLabelLists(rows.map((r) => r.labels))
    )
    return merged.map((e) => ({
      value: e.label,
      label: e.label,
      count: e.count
    }))
  }, [apiCatalog, rows])

  const filterActive = search.length > 0 || labelFilter.length > 0 || statuses.length > 0
  const isLoading = customLoading || rulesLoading

  const openRow = (row: UnifiedRow) => {
    void navigate(`/configure?type=${row.type}&id=${row.id}`)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <FleetSummary
          total={counts.total}
          items={[
            {
              key: 'active',
              label: 'active',
              count: counts.active,
              dotClass: 'bg-st-run',
              countClass: 'text-st-run'
            },
            {
              key: 'paused',
              label: 'paused',
              count: counts.paused,
              dotClass: 'bg-st-warn',
              countClass: 'text-st-warn'
            }
          ]}
        />
        <div className="ml-auto flex items-center gap-2">
          <CreateConfigActions
            manualOptions={MANUAL_CREATE_OPTIONS.map(({ label, route }) => ({ label, route }))}
            aiOptions={AI_DRAFT_OPTIONS.map(({ label, promptTemplate }) => ({
              label,
              promptTemplate
            }))}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-bg-elev px-3 py-2.5 shadow-sm">
        <div className="relative min-w-45 flex-[0_1_13.75rem]">
          <Input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
            placeholder="Search configs…"
            aria-label="Search configs"
            className="h-8 pl-8 text-sm"
          />
          <ListFilterIcon className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-4" />
        </div>
        <MultiSelectFilter
          label="Labels"
          icon={TagIcon}
          options={labelOptions}
          selected={labelFilter}
          onChange={(next) => {
            setLabelFilter(next)
            setPage(0)
          }}
          searchable
          searchPlaceholder="Search labels"
          emptyLabel="No labels match"
        />
        <MultiSelectFilter
          label="Status"
          icon={CircleDotIcon}
          options={statusOptions}
          selected={statuses}
          onChange={(next) => {
            setStatuses(next)
            setPage(0)
          }}
        />
        <div className="ml-auto">
          <SortMenu value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} />
        </div>
      </div>

      {filterActive && (
        <div className="-mt-2 flex items-center gap-2.5 text-xs text-ink-3">
          <span>
            Showing {filtered.length} of {rows.length}
            {labelFilter.length > 0 && ` · labels: ${labelFilter.join(', ')}`}
            {statuses.length > 0 && ` · status: ${statuses.join(', ')}`}
            {search.trim() && ` · “${search.trim()}”`}
          </span>
          <button
            type="button"
            onClick={() => {
              setSearch('')
              setLabelFilter([])
              setStatuses([])
              setPage(0)
            }}
            className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-line px-2 py-0.5 text-11 text-ink-2"
          >
            Clear
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-[0.625rem] border border-line bg-bg-elev">
        <div className="min-w-250">
          <div className="grid h-9 grid-cols-[minmax(12.5rem,2.4fr)_minmax(9.375rem,1fr)_minmax(9.375rem,1.6fr)_5.75rem_6rem_6rem] items-center gap-4 border-b border-line px-4 text-10 font-semibold tracking-wider text-ink-4 uppercase">
            <span>Name</span>
            <span>Type</span>
            <span>Labels</span>
            <span>Status</span>
            <span className="text-right">Last fired</span>
            <span className="text-right">Updated</span>
          </div>
          {isLoading ? (
            <div className="px-4 py-8 text-sm text-ink-3">Loading configs…</div>
          ) : pageRows.length === 0 ? (
            <div className="flex flex-col items-center gap-2.5 border-t border-line px-6 py-7">
              <span className="text-13 text-ink-3">
                No matching configs — adjust the label filter, status, or search.
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearch('')
                  setLabelFilter([])
                  setStatuses([])
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            pageRows.map((row) => {
              const tone = statusTone(row.state)
              const shown = row.labels.slice(0, 2)
              const more = row.labels.length - shown.length
              return (
                <div
                  key={`${row.type}:${row.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRow(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openRow(row)
                    }
                  }}
                  className="grid min-h-11.5 cursor-pointer grid-cols-[minmax(12.5rem,2.4fr)_minmax(9.375rem,1fr)_minmax(9.375rem,1.6fr)_5.75rem_6rem_6rem] items-center gap-4 border-t border-line px-4 hover:bg-bg-sunk"
                >
                  <div className="flex min-w-0 flex-col gap-0.5 py-2">
                    <span className="flex min-w-0 items-center gap-1.5 truncate text-13 font-medium text-ink">
                      <span className="truncate">{row.name}</span>
                      {row.builtIn ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-9 font-semibold text-primary">
                          <SparklesIcon className="size-2.5" />
                          Built-in
                        </span>
                      ) : null}
                    </span>
                    <span className="truncate text-12 text-ink-4">{row.description || '—'}</span>
                  </div>
                  <span className="truncate text-12 text-ink">
                    {row.builtIn ? 'Built-in custom agent' : TYPE_LABELS[row.type]}
                  </span>
                  <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                    {shown.map((t) => (
                      <LabelPill key={t} label={t} active={labelFilter.includes(t)} />
                    ))}
                    {more > 0 && <span className="text-11 text-ink-4">+{more}</span>}
                  </div>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 text-12 font-medium whitespace-nowrap',
                      tone.fg
                    )}
                  >
                    <span
                      className={cn(
                        'size-1.5 shrink-0 rounded-full',
                        tone.fg.replace('text-', 'bg-')
                      )}
                    />
                    {tone.label}
                  </span>
                  <span className="text-right text-12 text-ink-3 tabular-nums">
                    {row.lastFiredAt ? formatRelativeTime(row.lastFiredAt) : '—'}
                  </span>
                  <span className="text-right text-12 text-ink-4 tabular-nums">
                    {formatRelativeTime(row.updatedAt)}
                  </span>
                </div>
              )
            })
          )}
        </div>
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center gap-2">
          <span className="text-11 text-ink-3 tabular-nums">
            {pageSafe * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE + PAGE_SIZE, filtered.length)}{' '}
            of {filtered.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto size-7 p-0"
            disabled={pageSafe === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="Previous page"
          >
            ‹
          </Button>
          <span className="text-11 text-ink-2 tabular-nums">
            {pageSafe + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="size-7 p-0"
            disabled={pageSafe >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            aria-label="Next page"
          >
            ›
          </Button>
        </div>
      )}
    </div>
  )
}
